import {Box, Typography} from '@mui/material';
import {Panel} from '../docViewerUtils';
import React, {
  ReactElement,
  Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as ThreeDotMenu} from '../../../assets/images/three-dot-menu.svg';

import {ReactComponent as PopOutIcon} from '../../../assets/images/pop-out-icon.svg';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../common/arr-menu/ArrMenu';
import CloseIcon from '@mui/icons-material/Close';
import './DraggablePanel.scss';
import {
  PortalPopoutContent,
  PortalPopoutTrigger,
  MUIPopoutProvider,
} from '../../PortalPopout';
import {createUUID} from '../../PortalPopout/utils/others';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {t} from 'i18next';
import ArrTabFocusProvider from '../../common/arr-tab-focus/ArrTabFocusProvider';

interface DraggablePanelProp {
  panel?: Panel;
  side: string;
  draggingId: string | null;
  onDragStart: (
    e: React.DragEvent<HTMLDivElement>,
    panelId: string,
    from: string
  ) => void;
  onDragEnd: () => void;
  height: number;
  isPopoutOpen?: boolean;
  togglePopout?: (id: string, popoutState?: boolean) => void;
  closePopoutOnReload?: boolean;
  popoutWidth?: number;
  popoutHeight?: number;
  style?: {[key: string]: string};
  className?: string;
  onHeaderKeyDown?: (key: string, panelId: string, side: string) => void;
}

interface TitleBarProps {
  panel: Panel;
  menuRef?: {
    current: ArrMenuHandle | null;
  };
  titleRef?: Ref<HTMLDivElement | ReactElement | null>;
  onMenuClick: (currentTarget: HTMLElement, autoOpen?: boolean) => void;
  onItemClick?: (item: CustomMenuItem) => void;
  isPopoutOpen?: boolean;
  onPopout: (val: boolean) => void;
  onMouseDown: () => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
  onTouchStart: () => void;
  onTouchEnd: () => void;
  popoutId: string;
  onHeaderKeyDown?: (key: string, panelId: string) => void;
}

const LONG_PRESS_DELAY = 100; // milliseconds

// ----------------------------------
// TitleBar Component
// ----------------------------------
const TitleBar: React.FC<TitleBarProps> = ({
  panel,
  menuRef,
  titleRef,
  onMenuClick,
  onItemClick,
  onPopout,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
  popoutId,
  onHeaderKeyDown,
}) => {
  const menuIconRef = useRef<HTMLButtonElement | null>(null);
  const [keyboardMode, setKeyboardMode] = useState<boolean>(false);

  useEffect(() => {
    // Trigger a menu open to register the ref elm on the current window.
    onMenuClick(menuIconRef.current as HTMLElement, false);
    setTimeout(() => {
      menuRef?.current?.close();
    }, 100);
  }, []);
  // -----------------------

  const {
    //
    isAllowPopoutPanels,
  } = useSelector((state: RootState) => state.docView.docViewConfiguration);

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLElement>) => {
    switch (ev.key) {
      case 'Enter':
        ev.stopPropagation();
        setKeyboardMode(true);
        break;
      case 'Escape':
        ev.stopPropagation();
        setKeyboardMode(false);
        break;
      case 'Tab':
        setKeyboardMode(false);
        break;
      default:
        if (keyboardMode) {
          onHeaderKeyDown?.(ev.key, panel.id);
        }
        break;
    }
  };

  return (
    <Box
      ref={titleRef}
      className={`panel-header ${keyboardMode ? 'keyboard-mode' : ''}`}
      data-testid={`draggable-header-${panel.id}`}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onKeyDown={handleKeyDown}
      onBlur={() => setKeyboardMode(false)}
      sx={{
        userSelect: 'none',
      }}
      aria-live="assertive"
      aria-label={`Draggable Header ${panel.title}`}
      data-tab-id={`header-${panel.id}`}
      data-tab-group={panel.id}
    >
      <Box className="draggable-header-title">
        <Typography
          className="typo-text subhead5_size draggable-title"
          data-testid={`draggable-title-${panel.id}`}
        >
          {panel.title}
        </Typography>
      </Box>
      <Box className="controls" onKeyDown={(ev) => ev.stopPropagation()}>
        <ArrMenu
          className="docview-menu"
          id="arr-menu-1"
          ref={menuRef}
          items={panel.menuOptions}
          onItemClick={onItemClick}
          direction="bottom-right"
          aria-haspopup="true"
          tabGroup={panel.id}
        />
        {panel.menuOptions.length > 0 && (
          <ArrIconButton
            tooltipContent={t('docView.menu')}
            tooltipPlacement="top"
            tooltipOffset={10}
            tooltipFocusableWrapper={true}
            icon={<ThreeDotMenu />}
            onClick={() => onMenuClick(menuIconRef.current as HTMLElement)}
            ariaLabel={`Menu options ${panel.title}`}
            shape="none"
            size="medium"
            ref={menuIconRef}
            data-testid={`draggable-menu-${panel.id}`}
            testId={`draggable-menu-${panel.id}`}
            aria-haspopup="true"
            role="button"
            data-tab-group={panel.id}
            data-tab-id={`draggable-menu-${panel.id}`}
            onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onMenuClick(menuIconRef.current as HTMLElement);
              }
            }}
          />
        )}

        {isAllowPopoutPanels && (
          <PortalPopoutTrigger
            popoutId={popoutId}
            options={{
              title: panel.title,
            }}
          >
            {({isOpen, toggle}) => (
              <ArrIconButton
                tooltipContent={
                  isOpen ? t('docView.closePopout') : t('docView.popout')
                }
                tooltipPlacement="top"
                tooltipOffset={10}
                tooltipFocusableWrapper={true}
                icon={isOpen ? <CloseIcon /> : <PopOutIcon />}
                onClick={() => {
                  toggle();
                  onPopout && onPopout(!isOpen);
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    toggle();
                    onPopout && onPopout(!isOpen);
                  }
                }}
                ariaLabel={`Pop out ${panel.title}`}
                shape="none"
                size="medium"
                data-testid={`draggable-popout-${panel.id}`}
                testId={`draggable-popout-${panel.id}`}
                role="button"
                data-tab-group={panel.id}
                data-tab-id={`draggable-menu-${panel.id}`}
              />
            )}
          </PortalPopoutTrigger>
        )}
      </Box>
    </Box>
  );
};

// ----------------------------------
// DraggablePanel Component
// ----------------------------------
const DraggablePanel = ({
  panel,
  side,
  draggingId,
  onDragStart,
  onDragEnd,
  togglePopout,
  height,
  isPopoutOpen = false,
  style,
  className,
  onHeaderKeyDown,
}: DraggablePanelProp) => {
  const [, setPopoutOpen] = useState<boolean>(isPopoutOpen);
  const [popoutSize] = useState<{width: number; height: number}>({
    width: 0,
    height: 0,
  });

  const [isDraggable, setIsDraggable] = useState(false);
  const [isLongPressReady, setIsLongPressReady] = useState(false);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  const uuid = useMemo(() => createUUID(), []);
  const panelId = useMemo(() => (panel ? panel.id : `panel-${uuid}`), [panel]);
  // -----------------------

  const handlePressStart = () => {
    pressTimer.current = setTimeout(() => {
      setIsDraggable(true);
      setIsLongPressReady(true);
    }, LONG_PRESS_DELAY);
  };
  // -----------------------

  const handlePressEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    setIsDraggable(false);
    setIsLongPressReady(false);
  };
  // -----------------------

  const isDragging = draggingId === panel?.id;

  const openMenu = (currentTarget: HTMLElement, autoOpen?: boolean) => {
    if (menuRef1?.current) {
      menuRef1.current.open(currentTarget as HTMLElement, autoOpen);
    }
  };
  // -----------------------

  const onItemClick = (item: CustomMenuItem) => {
    item?.onClick?.();
  };
  // -----------------------

  return panel ? (
    <Box
      className={`draggable-panel ${className} ${isLongPressReady ? 'drag-ready' : ''}`}
      style={{
        flexBasis: height ? `${height}%` : 'auto',
        ...style,
      }}
      data-testid={`draggable-${panel.id}`}
      draggable={isDraggable}
      onDragStart={(e) => {
        setIsLongPressReady(false);
        onDragStart(e, panel.id, side);
      }}
      onDragEnd={onDragEnd}
      aria-label={panel.title}
      data-tab-group="doc-view"
      data-tab-id={panel.id}
    >
      {isDragging ? (
        <Box
          className="placeholder"
          data-testid={`draggable-placeholder-${panel.id}`}
        />
      ) : (
        <PortalPopoutContent
          popoutId={panelId}
          placeholder={null}
          className="popout-content"
        >
          <ArrTabFocusProvider id={panel.id}>
            <MUIPopoutProvider>
              <TitleBar
                onMouseDown={handlePressStart}
                onMouseUp={handlePressEnd}
                onMouseLeave={handlePressEnd}
                onTouchStart={handlePressStart}
                onTouchEnd={handlePressEnd}
                panel={panel}
                menuRef={menuRef1}
                onMenuClick={openMenu}
                onItemClick={onItemClick}
                onHeaderKeyDown={(...args) => onHeaderKeyDown?.(...args, side)}
                popoutId={panelId}
                onPopout={(val) => {
                  setPopoutOpen(val);
                  togglePopout && togglePopout(panel?.id, val);
                }}
              />
              {typeof panel.component === 'function'
                ? panel.component({
                    ...popoutSize,
                    panelId: panel.id,
                  })
                : panel.component}
            </MUIPopoutProvider>
          </ArrTabFocusProvider>
        </PortalPopoutContent>
      )}
    </Box>
  ) : null;
};

export default React.memo(DraggablePanel);
